import TimeInput from './TimeInput';
import { I18nTranslate } from '@i18n';
import moment from 'moment';
import { useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import { buttonTheme } from '@redact/materialUITheme';
import { useSelector } from 'react-redux';
import {
  selectGlobalSettings,
  selectCurrentPosition,
  selectVoiceMaskMediaUrl,
} from '@common/state/modules/mediaDetails';
import {
  Box,
  Button,
  ButtonBase,
  Grid2 as Grid,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import {
  AudioEditSlice,
  BlurLevel,
  AudioEditType,
} from '@common-modules/mediaDetails/models';
import { useStyles } from './styles';
import { selectFeatureFlags } from '@common/user-permissions';
import { BackgroundBlurSlice } from '@common/state/modules/mediaDetails/store.models';
import { numberToBlurLevel } from '@common/shared/util';
import { DEFAULT_BACKGROUND_BLUR_LEVEL } from '@helpers/constants';

const style = {
  button: {
    '&.MuiButton-textPrimary': {
      color: 'primary.main',
    },
    '&.MuiButton-containedPrimary': {
      color: 'primary.contrastText',
      borderRadius: '4px',
    },
    padding: '2px 6px',
    textTransform: 'none',
  },
  timeQuickSet: {
    '&.MuiTypography-root': {
      fontSize: '11px',
      color: 'primary.main',
      textDecoration: 'underline',
    },
  },
  timeQuickSetSelected: {
    '&.MuiTypography-root': {
      fontSize: '11px',
      color: 'primary.main',
      fontWeight: 600,
      textDecoration: 'underline',
    },
  },
  infoText: {
    '&.MuiTypography-root': {
      fontSize: '10px',
    },
  },
  errorText: {
    '&.MuiTypography-root': {
      color: '#FF4444',
    },
  },
};

export const extraStyles = {
  // container: {
  //   padding: '0 8px',
  //   width: '207px',
  //   display: 'flex',
  //   flexDirection: 'column',
  //   borderRadius: '4px',
  // },
  // button: {
  //   '&.MuiButton-textPrimary': {
  //     color: 'primary.main',
  //     marginTop: '4px',
  //     marginRight: '4px',
  //   },
  //   '&.MuiButton-textSecondary': {
  //     color: '#525252',
  //     marginTop: '4px',
  //     marginRight: '4px',
  //   },
  //   '&.MuiButton-containedPrimary': {
  //     color: 'primary.contrastText',
  //     backgroundColor: 'primary.main',
  //     marginTop: '4px',
  //   },
  //   '&.Mui-disabled': {
  //     color: 'text.disabled',
  //     background: 'rgba(0, 0, 0, 0.12)',
  //   },
  //   fontSize: '12px',
  //   borderRadius: '4px',
  //   textTransform: 'none',
  // },
  // selectionContainer: {
  //   display: 'flex',
  //   flexDirection: 'column',
  //   marginBottom: '2px',
  //   gap: '10px',
  // },
  selectionRow: {
    display: 'flex',
    flexDirection: 'row',
    // justifyContent: 'space-between',
    justifyContent: 'space-start',
    alignItems: 'center',
  },
  label: {
    color: '#212121',
    fontFamily: 'Nunito',
    fontSize: '13px',
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '20px',
    height: '100%',
    width: '78px',
    whiteSpace: 'nowrap',
  },
  // menuItem: {
  //   '&.MuiMenuItem-root': {
  //     display: 'flex',
  //     alignItems: 'center',
  //     px: 2,
  //     py: 0.75,
  //     justifyContent: 'flex-start',
  //     fontSize: '12px',
  //     fontStyle: 'normal',
  //     lineHeight: '20px',
  //     color: '#465364',
  //   },
  //   '&.MuiButtonBase-root-MuiMenuItem-root:hover': {
  //     backgroundColor: 'rgba(0, 0, 0, 0.04)',
  //   },
  //   '&.MuiMenuItem-root.Mui-selected': {
  //     backgroundColor: 'rgba(0, 0, 0, 0.08)',
  //   },
  // },
  // select: {
  //   color: '#465364',
  //   fontSize: '14px',
  // },
  textField: {
    '&.MuiTextField-root .MuiInputBase-input': {
      color: '#212121',
      fontSize: '14px',
      padding: '6px 4px 7px',
      // height: '36px',
      width: '40px',
      textAlign: 'right',
    },
  },
  units: {
    color: '#757575',
    fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
    fontSize: '10px',
    fontWeight: '400',
    lineHeight: '27px',
    verticalAlign: 'center',
  },
  // iconButton: {
  //   '&.MuiIconButton-root': {
  //     padding: '6px',
  //   },
  // },
  // title: {
  //   fontSize: '14px',
  //   fontWeight: '500',
  //   color: 'rgba(0, 0, 0, 0.87)',
  //   fontFamily: ' Roboto, Arial, Helvetica, sans-serif',
  // },
  // popoverTitle: {
  //   display: 'flex',
  //   justifyContent: 'space-between',
  //   alignItems: 'center',
  //   marginBottom: '2px',
  // },
};

let lastBlurLevel = undefined as BlurLevel | undefined;
let lastDefaultBackgroundBlurLevel = DEFAULT_BACKGROUND_BLUR_LEVEL;

const ManualRedactionForm = ({
  minMs,
  maxMs,
  onCancel,
  onSetRangeTime,
  // onChange, // unclear what this was used for in the past
  handleResetSlice,
  timeRange,
  hideGroupButton,
  isBlurForm,
}: ManualRedactionFormPropTypes) => {
  const classes = useStyles();
  const intl = useIntl();
  const voiceMaskMediaUrl = useSelector(selectVoiceMaskMediaUrl);
  // const voiceMask = voiceMaskMediaUrl !== undefined; option to hide button when no voice mask media
  const { voiceMask } = useSelector(selectFeatureFlags);

  const settings = useSelector(selectGlobalSettings);
  const defaultBackgroundBlurLevel =
    settings.defaultBackgroundBlurLevel || DEFAULT_BACKGROUND_BLUR_LEVEL;

  // every time user changes the default background blur level reset the lastBlurLevel to match
  if (defaultBackgroundBlurLevel !== lastDefaultBackgroundBlurLevel) {
    lastBlurLevel = defaultBackgroundBlurLevel;
  }

  lastDefaultBackgroundBlurLevel = defaultBackgroundBlurLevel;

  const [start, setLocalTimeRangeStart] = useState(timeRange[0]);
  const [end, setLocalTimeRangeEnd] = useState(timeRange[1]);

  const currentTimeMs = Math.floor(useSelector(selectCurrentPosition));

  // if editing slice start with previous blur level - otherwise use last submitted blurLevel
  let currentBlurLevel = undefined;
  if (timeRange[2] && 'blurLevel' in timeRange[2]) {
    currentBlurLevel = timeRange[2].blurLevel;
  }
  const [blurLevel, setLocalBlurLevel] = useState(
    currentBlurLevel || lastBlurLevel || defaultBackgroundBlurLevel
  );

  const [errorStart, setErrorsStart] = useState<string | null>(null);
  const [errorEnd, setErrorsEnd] = useState<string | null>(null);

  useEffect(() => {
    setLocalTimeRangeStart(timeRange[0]);
    setLocalTimeRangeEnd(timeRange[1]);
  }, [timeRange]);

  const onSubmit = (type: AudioEditType) => {
    onSetRangeTime(start, end, { type });
  };

  const onSubmitBlur = () => {
    lastBlurLevel = blurLevel;
    onSetRangeTime(start, end, { blurLevel });
  };

  const isDisableSave = useMemo(() => {
    if (errorStart || errorEnd) {
      return true;
    }
  }, [errorStart, errorEnd]);

  const formatTimeString = (timeMs: number) => {
    const t = moment.duration(timeMs);
    if (!timeMs) {
      return '0 ms';
    } else if (t.hours() > 0) {
      return moment.utc(timeMs).format('h:mm:ss:SSS');
    } else {
      return moment.utc(timeMs).format('mm:ss:SSS');
    }
  };

  const checkTimeErrors = (
    startTimeMs: number,
    endTimeMs: number,
    isAdjustingStart: boolean
  ) => {
    // -------- check start time errors

    if (startTimeMs < minMs) {
      setErrorsStart(
        intl.formatMessage({
          id: 'errorExceededName',
          defaultMessage: 'Maximum value: exceeded',
        })
      );
    } else if (
      (startTimeMs >= endTimeMs && isAdjustingStart) ||
      startTimeMs >= maxMs
    ) {
      setErrorsStart(
        intl.formatMessage(
          { id: 'maxVal', defaultMessage: 'Maximum value: {value}' },
          { value: formatTimeString(Math.min(endTimeMs, maxMs) - 1) } // BTODO frame lock
        )
      );
    } else {
      setErrorsStart(null);
    }

    // ------ check end time errors
    if (endTimeMs > maxMs) {
      setErrorsEnd(
        intl.formatMessage({
          id: 'errorExceededName',
          defaultMessage: 'Maximum value: exceeded',
        })
      );
    } else if (
      endTimeMs <= startTimeMs &&
      startTimeMs < maxMs &&
      !isAdjustingStart
    ) {
      setErrorsEnd(
        intl.formatMessage(
          { id: 'minVal', defaultMessage: 'Minimum value: {value}' },
          { value: formatTimeString(startTimeMs + 1) }
        )
      );
    } else {
      setErrorsEnd(null);
    }
  };

  const onChangeTimeInputStart = (startTimeMs: number) => {
    checkTimeErrors(startTimeMs, end, true);
    setLocalTimeRangeStart(startTimeMs);
  };

  const onChangeTimeInputEnd = (endTimeMs: number) => {
    checkTimeErrors(start, endTimeMs, false);
    setLocalTimeRangeEnd(endTimeMs);
  };

  const onhandleResetSlice = () => {
    handleResetSlice(start, end);
  };

  return (
    <Grid container direction="column" className={classes.formContent}>
      <Grid>
        <Grid
          container
          alignItems="center"
          style={{ height: '100%' }}
          gridColumn={2}
          columnGap={'40px'}
          data-testid="manual-redaction-form-grid-container"
        >
          <Grid direction="column">
            <Grid>
              <TimeInput
                label="Start"
                value={start}
                onChange={onChangeTimeInputStart}
                textHelper="(hh:mm:ss.SSS)"
              />
            </Grid>
            <span
              style={{
                height: '22px',
                alignItems: 'center',
                display: 'flex',
                marginBottom: '3px',
              }}
            >
              <ButtonBase>
                <Typography
                  sx={[
                    start === minMs
                      ? style.timeQuickSetSelected
                      : style.timeQuickSet,
                  ]}
                  data-testid="timeline-range-editor-set-to-mix"
                  onClick={() => onChangeTimeInputStart(minMs)}
                >
                  {I18nTranslate.TranslateMessage('setToMinValue')}
                </Typography>
              </ButtonBase>
              <Typography
                sx={[
                  {
                    marginLeft: '8px',
                    marginRight: '8px',
                    fontSize: '11px',
                    color: 'grey',
                  },
                ]}
              >
                {I18nTranslate.TranslateMessage('or')}
              </Typography>
              <ButtonBase>
                <Typography
                  sx={[
                    style.timeQuickSet,
                    start === currentTimeMs && style.timeQuickSetSelected,
                  ]}
                  data-testid="timeline-range-editor-set-start-to-current"
                  onClick={() => onChangeTimeInputStart(currentTimeMs)}
                >
                  {I18nTranslate.TranslateMessage('currentTime')}
                </Typography>
              </ButtonBase>
            </span>
            <Typography
              sx={[style.infoText, errorStart !== null && style.errorText]}
              data-testid="timeline-range-editor-error-start-text"
            >
              {errorStart === null
                ? I18nTranslate.TranslateMessage('minVal', {
                    value: formatTimeString(minMs),
                  })
                : errorStart}
            </Typography>
          </Grid>
          <Grid direction="column">
            <TimeInput
              label="End"
              value={end}
              onChange={onChangeTimeInputEnd}
              textHelper="(hh:mm:ss.SSS)"
            />
            <span
              style={{
                height: '22px',
                alignItems: 'center',
                display: 'flex',
                marginBottom: '3px',
              }}
            >
              <ButtonBase>
                <Typography
                  sx={[
                    end === maxMs && style.timeQuickSetSelected,
                    style.timeQuickSet,
                  ]}
                  data-testid="timeline-range-editor-set-to-max"
                  onClick={() => onChangeTimeInputEnd(maxMs)}
                >
                  {I18nTranslate.TranslateMessage('setToMaxValue')}
                </Typography>
              </ButtonBase>
              {currentTimeMs > minMs && (
                <Typography
                  sx={[
                    {
                      marginLeft: '8px',
                      marginRight: '8px',
                      fontSize: '11px',
                      color: 'grey',
                    },
                  ]}
                >
                  {I18nTranslate.TranslateMessage('or')}
                </Typography>
              )}
              {currentTimeMs > minMs && (
                <ButtonBase>
                  <Typography
                    sx={[
                      style.timeQuickSet,
                      end === currentTimeMs && style.timeQuickSetSelected,
                    ]}
                    data-testid="timeline-range-editor-set-end-to-current"
                    onClick={() => onChangeTimeInputEnd(currentTimeMs)}
                  >
                    {I18nTranslate.TranslateMessage('currentTime')}
                  </Typography>
                </ButtonBase>
              )}
            </span>

            <Typography
              sx={[style.infoText, errorEnd !== null && style.errorText]}
              data-testid="timeline-range-editor-error-end-text"
            >
              {errorEnd === null
                ? I18nTranslate.TranslateMessage('maxVal', {
                    value: formatTimeString(maxMs),
                  })
                : errorEnd}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {!!isBlurForm && !hideGroupButton && (
        <Grid>
          <Box sx={extraStyles.selectionRow} paddingTop="14px">
            <Box sx={extraStyles.label}>
              {I18nTranslate.TranslateMessage('blurLevel')}:
            </Box>
            <Box
              sx={extraStyles.selectionRow}
              gap={1}
              data-testid="blur-level-container"
            >
              <Box sx={extraStyles.units}>
                (
                {I18nTranslate.TranslateMessage('aTob', {
                  a: '1',
                  b: '10',
                })}
                )
              </Box>
              <TextField
                data-testid="render-number-text"
                value={blurLevel + ''}
                type={'number'}
                slotProps={{
                  htmlInput: {
                    min: 1,
                    max: 10,
                    style: { fontSize: 14, textAlign: 'right' },
                  },
                }}
                onChange={(event) => {
                  const newVal = Number(event?.target.value);
                  const level = numberToBlurLevel(newVal);
                  setLocalBlurLevel(level);
                }}
                sx={extraStyles.textField}
                variant={'outlined'}
                // size="small"
              />
            </Box>
          </Box>
        </Grid>
      )}
      {!hideGroupButton && (
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginTop: '6px' }}
        >
          <Grid>
            <ThemeProvider theme={buttonTheme}>
              <Button
                className={classes.unredactButton}
                onClick={onhandleResetSlice}
                data-testid="timeline-range-editor-button-reset"
              >
                <DeleteIcon />
              </Button>
            </ThemeProvider>
          </Grid>
          <Grid>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Grid>
                <ThemeProvider theme={buttonTheme}>
                  <Button
                    color="primary"
                    onClick={onCancel}
                    data-testid="timeline-range-editor-button-cancel"
                    sx={style.button}
                  >
                    {I18nTranslate.TranslateMessage('cancel')}
                  </Button>
                </ThemeProvider>
              </Grid>
              {!!isBlurForm && (
                <Grid>
                  <ThemeProvider theme={buttonTheme}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onSubmitBlur()}
                      data-testid="timeline-range-editor-button-blur-submit"
                      sx={style.button}
                      disabled={isDisableSave}
                    >
                      {I18nTranslate.TranslateMessage('apply')}
                    </Button>
                  </ThemeProvider>
                </Grid>
              )}
              {!isBlurForm && voiceMask && (
                <Grid>
                  <ThemeProvider theme={buttonTheme}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onSubmit(AudioEditType.MASK_VOICE)}
                      data-testid="timeline-range-editor-button-voice-mask-submit"
                      sx={style.button}
                      disabled={isDisableSave || !voiceMaskMediaUrl}
                    >
                      {I18nTranslate.TranslateMessage('maskVoice')}
                    </Button>
                  </ThemeProvider>
                </Grid>
              )}
              {!isBlurForm && (
                <Grid>
                  <ThemeProvider theme={buttonTheme}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onSubmit(AudioEditType.REDACT)}
                      data-testid="timeline-range-editor-button-redact-submit"
                      sx={style.button}
                      disabled={isDisableSave}
                    >
                      {I18nTranslate.TranslateMessage('redact')}
                    </Button>
                  </ThemeProvider>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export interface ManualRedactionFormPropTypes {
  readonly minMs: number;
  readonly maxMs: number;
  readonly timeRange: AudioEditSlice | BackgroundBlurSlice;
  readonly hideGroupButton: boolean;
  readonly onCancel: () => void;
  readonly onChange: (tr: [start: number, end: number]) => void;
  readonly onSetRangeTime: (
    start: number,
    end: number,
    options: {
      type?: AudioEditType;
      blurLevel?: BlurLevel;
    }
  ) => void;
  readonly handleResetSlice: (start: number, end: number) => void;
  readonly isBlurForm?: boolean;
}

export default ManualRedactionForm;
